import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { MagnifyingGlass, Broadcast, List, X, DownloadSimple, CloudSlash, UserCircle, SignOut, SignIn } from "@phosphor-icons/react";
import { fetchActiveLive } from "../lib/api";
import { useOnlineStatus } from "../lib/offline";
import { useAuth } from "../context/AuthContext";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/category/nohay", label: "Nohay" },
  { to: "/category/majalis-lectures", label: "Majalis" },
  { to: "/category/manqabat", label: "Manqabat" },
  { to: "/category/marsiya", label: "Marsiya" },
  { to: "/category/holy-quran", label: "Quran" },
  { to: "/news", label: "News" },
];

export default function Header() {
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [online, setOnline] = useState(true);
  const nav = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useOnlineStatus(setOnline);
  }, []);

  useEffect(() => {
    fetchActiveLive().then(setActive).catch(() => {});
    const t = setInterval(() => fetchActiveLive().then(setActive).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 glass border-b border-white/10" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center gap-8">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <img src="/logo.svg" alt="ShiaTunes" className="w-9 h-9" />
          <span className="font-serif text-2xl tracking-tight">
            <span className="text-ivory">Shia</span>
            <span className="text-[#ef4444] italic">Tunes</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7 ml-2">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${isActive ? "text-ivory" : "text-[#A1A1AA] hover:text-ivory"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex-1" />

        <form onSubmit={onSearch} className="hidden md:flex items-center gap-2 px-3 h-10 bg-[#121214] border border-[#2A2A2D] focus-within:border-[#B79C7B] transition-colors">
          <MagnifyingGlass size={16} weight="duotone" className="text-[#71717A]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search reciters, nohay, articles..."
            data-testid="header-search-input"
            className="bg-transparent outline-none text-sm w-56 placeholder:text-[#71717A]"
          />
        </form>

        <Link
          to="/live"
          data-testid="header-live-link"
          className="hidden md:inline-flex items-center gap-2 h-10 px-4 border border-[#8A2E2E] hover:bg-[#8A2E2E] transition-colors text-sm tracking-wide group"
        >
          <span className="relative inline-flex">
            <Broadcast size={16} weight="duotone" className="text-[#FF3B30] group-hover:text-ivory" />
            {active?.is_live && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#FF3B30] animate-pulseLive" />}
          </span>
          {active?.is_live ? <span className="text-ivory">Watch Live</span> : <span>Live</span>}
        </Link>

        <Link
          to="/downloads"
          title="Offline library"
          data-testid="header-downloads-link"
          className="hidden md:inline-flex items-center justify-center w-10 h-10 border border-[#2A2A2D] hover:border-[#B79C7B] hover:text-[#B79C7B] transition-colors"
        >
          <DownloadSimple size={16} weight="duotone" />
        </Link>

        {!online && (
          <span className="hidden md:inline-flex items-center gap-2 h-10 px-3 border border-[#B79C7B]/40 text-[#B79C7B] text-xs tracking-wide" data-testid="offline-indicator">
            <CloudSlash size={14} weight="duotone" /> Offline
          </span>
        )}

        {user ? (
          <div className="hidden md:flex items-center gap-2">
            <Link to="/dashboard" className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-ivory px-3 h-10 border border-[#2A2A2D] hover:border-[#B79C7B] transition-colors">
              <UserCircle size={16} weight="duotone" className="text-[#B79C7B]" />
              {user.username}
              <span className="text-[10px] font-mono uppercase text-[#71717A]">{user.role}</span>
            </Link>
            <button
              onClick={() => { logout(); nav("/"); }}
              title="Logout"
              className="flex items-center justify-center w-10 h-10 border border-[#2A2A2D] hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
            >
              <SignOut size={16} weight="duotone" />
            </button>
          </div>
        ) : (
          <Link
            to="/login"
            className="hidden md:inline-flex items-center gap-2 h-10 px-4 border border-[#2A2A2D] hover:border-[#B79C7B] text-sm text-[#A1A1AA] hover:text-ivory transition-colors"
          >
            <SignIn size={16} weight="duotone" /> Login
          </Link>
        )}

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-ivory"
          data-testid="mobile-menu-button"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <List size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#0A0A0B]">
          <div className="px-6 py-4 flex flex-col gap-3">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="text-base text-[#A1A1AA] hover:text-ivory"
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink to="/live" onClick={() => setOpen(false)} className="text-base text-[#FF3B30]" data-testid="mobile-nav-live">
              Watch Live
            </NavLink>
            <NavLink to="/downloads" onClick={() => setOpen(false)} className="text-base text-[#A1A1AA] hover:text-ivory flex items-center gap-2" data-testid="mobile-nav-downloads">
              <DownloadSimple size={16} weight="duotone" /> Offline Library
            </NavLink>
            {user ? (
              <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="text-base text-[#A1A1AA] hover:text-[#FF3B30] flex items-center gap-2 text-left">
                <SignOut size={16} weight="duotone" /> Logout ({user.username})
              </button>
            ) : (
              <NavLink to="/login" onClick={() => setOpen(false)} className="text-base text-[#B79C7B] flex items-center gap-2">
                <SignIn size={16} weight="duotone" /> Login / Register
              </NavLink>
            )}
            {!online && (
              <div className="text-sm text-[#B79C7B] flex items-center gap-2 mt-2">
                <CloudSlash size={14} weight="duotone" /> You are offline — only saved tracks will play.
              </div>
            )}
            <form onSubmit={onSearch} className="flex items-center gap-2 mt-2 px-3 h-10 bg-[#121214] border border-[#2A2A2D]">
              <MagnifyingGlass size={16} className="text-[#71717A]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search..."
                className="bg-transparent outline-none text-sm flex-1 placeholder:text-[#71717A]"
                data-testid="mobile-search-input"
              />
            </form>
          </div>
        </div>
      )}
    </header>
  );
}
